import * as React from 'react';
import CheckboxBlankCircleBold from '@/assets/checkboxBlankCircleBold.svg';
import CheckboxCircleBold from '@/assets/checkboxCircleBold.svg';
import classNames from 'classnames';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  label?: React.ReactNode;
  checked?: boolean; // controlled
  defaultChecked?: boolean; // uncontrolled
  onChange?: (checked: boolean) => void;
  indeterminate?: boolean;
  size?: number; // pixel size of the square
  color?: string; // CSS color for checked fill
  className?: string;
  ref?: React.Ref<HTMLInputElement>; // React 19: ref as a prop
}
const Checkbox = ({
  id,
  label,
  checked,
  defaultChecked,
  onChange,
  disabled = false,
  indeterminate = false,
  size = 24,
  className,
  ref,
  ...rest
}: CheckboxProps) => {
  const autoId = React.useId();
  const inputId = id ?? autoId;
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // Compose internal ref with external ref prop
  const setRefs = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (!ref) return;
      if (typeof ref === 'function') ref(node);
      else
        try {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
        } catch {}
    },
    [ref],
  );

  const isControlled = typeof checked === 'boolean';
  const [internalChecked, setInternalChecked] = React.useState<boolean>(!!defaultChecked);
  const currentChecked = isControlled ? checked : internalChecked;

  React.useEffect(() => {
    if (inputRef.current) {
      // Only show native mixed state when not checked
      inputRef.current.indeterminate = indeterminate && !currentChecked;
    }
  }, [indeterminate, currentChecked]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!isControlled) setInternalChecked(e.target.checked);
    onChange?.(e.target.checked);
  }

  return (
    <label
      htmlFor={inputId}
      className={classNames(
        'checkbox relative inline-flex items-start gap-3 select-none',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
    >
      <input
        ref={setRefs}
        id={inputId}
        type="checkbox"
        className="peer absolute h-0 w-0 opacity-0"
        disabled={disabled}
        checked={isControlled ? checked : undefined}
        defaultChecked={!isControlled ? defaultChecked : undefined}
        onChange={handleChange}
        aria-checked={indeterminate ? 'mixed' : currentChecked ? 'true' : 'false'}
        {...rest}
      />

      {/* Visual box using Tailwind + external SVG icons */}
      <span
        className={classNames(
          'relative grid place-items-center rounded-xl',
          'peer-focus-visible:outline-none',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2',
        )}
        style={{ width: size, height: size }}
        aria-hidden
      >
        {/* Box background + border */}

        {indeterminate && !currentChecked ? (
          <div className="relative h-5.5 w-5.5">
            <CheckboxBlankCircleBold className="h-5.5 w-5.5 fill-white stroke-black" />
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold">−</span>
          </div>
        ) : currentChecked ? (
          <CheckboxCircleBold className="h-5.5 w-5.5" />
        ) : (
          <CheckboxBlankCircleBold className="h-5.5 w-5.5 fill-white stroke-black" />
        )}
      </span>

      {label ? <span className="label text-base leading-5">{label}</span> : null}
    </label>
  );
};

export default Checkbox;
